import React, { useEffect, useState } from 'react';
import { CheckCircle2, Save } from 'lucide-react';
import { AudienceLevel, Cohort, LEVEL_LABELS } from '../../types/pathway';
import { formatHours, totalHours } from '../../utils/pathwayMatching';
import { PathwayButton } from './PathwayButton';
interface ReviewSaveModalProps {
  open: boolean;
  pathwayName: string;
  company: string;
  items: string[];
  audience: AudienceLevel | 'any';
  cohort: Cohort | null;
  isUpdate: boolean;
  onCompanyChange: (value: string) => void;
  onSave: () => void;
  onBuildAnother: () => void;
  onViewLibrary: () => void;
  onClose: () => void;
}
export function ReviewSaveModal({
  open,
  pathwayName,
  company,
  items,
  audience,
  cohort,
  isUpdate,
  onCompanyChange,
  onSave,
  onBuildAnother,
  onViewLibrary,
  onClose
}: ReviewSaveModalProps) {
  const [saved, setSaved] = useState(false);
  const [savedAsUpdate, setSavedAsUpdate] = useState(isUpdate);
  useEffect(() => {
    if (open) {
      setSaved(false);
      setSavedAsUpdate(isUpdate);
    }
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !saved) onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onClose, saved]);
  if (!open) return null;
  const savePathway = () => {
    setSavedAsUpdate(isUpdate);
    onSave();
    setSaved(true);
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 p-4" role="dialog" aria-modal="true" aria-labelledby="review-save-title" onMouseDown={event => {
    if (event.target === event.currentTarget && !saved) onClose();
  }} data-id="element-1964" data-mp="z5lv3t3s5lep">
      <div className="w-full max-w-lg overflow-hidden rounded-xl bg-white shadow-xl" data-id="element-1965" data-mp="3ewhu471q5442">
        {saved ? <div className="p-7 text-center" data-id="element-1966" data-mp="39xwvgc24eshq">
            <CheckCircle2 size={36} className="mx-auto text-[#007558]" data-id="element-1967" data-mp="39z7y28jtxu79" />
            <h2 id="review-save-title" className="mt-4 text-xl font-bold text-gray-900" data-id="element-1968" data-mp="1dvbi221jiggg">
              {savedAsUpdate ? 'Pathway updated' : 'Pathway saved to your library'}
            </h2>
            <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-gray-600" data-id="element-1969" data-mp="1nsgagjlzb85p">
              {savedAsUpdate ? 'Your changes are ready in the library whenever you need to combine or share this pathway.' : 'You can keep building, or select this pathway with others in your library when you are ready to distribute it.'}
            </p>
            <div className="mt-6 grid gap-3 sm:grid-cols-2" data-id="element-1970" data-mp="26j7d72bgo0b4">
              <PathwayButton variant="secondary" onClick={onViewLibrary} data-id="element-1971" data-mp="26jpi0nc6vmun">
                View library
              </PathwayButton>
              <PathwayButton onClick={onBuildAnother} data-id="element-1972" data-mp="2fjvvi97w0mtt">
                Build another pathway
              </PathwayButton>
            </div>
          </div> : <>
            <div className="flex items-start justify-between border-b border-gray-200 p-6" data-id="element-1973" data-mp="2kgvxo91m95fn">
              <div data-id="element-1974" data-mp="8tiptbvxo7j7">
                <h2 id="review-save-title" className="text-xl font-bold text-gray-900" data-id="element-1975" data-mp="3wilp6in9euq">
                  Review &amp; save
                </h2>
                <p className="mt-1 text-sm text-gray-600" data-id="element-1976" data-mp="3w14b58sfnenq">
                  Save this pathway to your library. You can export it with other pathways later.
                </p>
              </div>
              <button type="button" onClick={onClose} className="text-sm font-semibold text-gray-700 underline transition-colors hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#30718D]" data-id="element-1977" data-mp="rl1stipyjll8">
                Close
              </button>
            </div>
            <div className="space-y-5 p-6" data-id="element-1978" data-mp="duyav3y42e1l">
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4" data-id="element-1979" data-mp="du6s4tpm4asq">
                <p className="text-sm font-semibold text-gray-900" data-id="element-1980" data-mp="1bb5h83883644">{pathwayName}</p>
                <p className="mt-1 text-sm text-gray-600" data-id="element-1981" data-mp="16iu9ie78qg10">
                  {items.length} {items.length === 1 ? 'module' : 'modules'} · {formatHours(totalHours(items))} · {audience === 'any' ? 'Mixed audience' : LEVEL_LABELS[audience]}
                </p>
                {cohort && <p className="mt-1 text-xs text-gray-500" data-id="element-1982" data-mp="v0l9f2e1l0d4">Cohort: {cohort.name}</p>}
              </div>
              <label className="block" data-id="element-1983" data-mp="m29jz3oww8m9">
                <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1984" data-mp="m3ath3qa4n1v">Company</span>
                <input value={company} onChange={event => onCompanyChange(event.target.value)} className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#30718D]" data-id="element-1985" data-mp="19sdl94emnwa3" />
              </label>
              <p className="rounded-lg border border-[#30718D]/20 bg-[#30718D]/5 px-3 py-2 text-xs leading-relaxed text-[#265a70]" data-id="element-1986" data-mp="1sq6grlmx93xg">
                This step only saves your work. Select one or more saved pathways from the library to generate a shareable link, PowerPoint, CSV, Word, or PDF.
              </p>
            </div>
            <div className="flex justify-end gap-3 border-t border-gray-200 p-6" data-id="element-1987" data-mp="22tv8tqd1prlj">
              <PathwayButton variant="secondary" onClick={onClose} data-id="element-1988" data-mp="2gkzzw42clbk9">Cancel</PathwayButton>
              <PathwayButton onClick={savePathway} data-id="element-1989" data-mp="2bmoq1tc1z121"><Save size={17} data-id="element-1990" data-mp="2bni0b4a808ox" />{isUpdate ? 'Save changes' : 'Save pathway'}</PathwayButton>
            </div>
          </>}
      </div>
    </div>;
}